import React, { Fragment, useCallback, useEffect, useState } from 'react';

import {
    App,
    Button,
    Divider,
    Drawer,
    Form,
    FormItemProps,
    FormProps,
    Input as AntdInput,
    Select,
    SelectProps} from 'antd';
import { MaskedInput } from 'antd-mask-input';

import { createSupplier, listSuppliers } from 'services/Supplier.service';

import Authentication from './Authentication';
import { makeFilterOption } from './helpers/Form.helper';
import { sleep } from './Sleep';
import WhatsAppNumberField from './WhatsAppNumberField';

type Props = {
    form: FormProps['form'],
    name: FormItemProps['name'],
    label?: FormItemProps['label'],
    multiple?: SelectProps['mode'],
    rules?: FormItemProps['rules'],
    extra?: FormItemProps['extra'],
};

type Values = {
    name: Supplier.Model['name'],
    cnpj?: Supplier.Model['cnpj'],
    email?: Supplier.Model['email'],
    phone_2?: Supplier.Model['phone_2'],
};

export function SupplierField({ form, name, label, multiple, rules, extra }: Props) {
    const [suppliers, setSuppliers] = useState<Supplier.Model[]>([]);
    const [isFetching, setIsFetching] = useState(false);

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [drawerForm] = Form.useForm<Values>();
    const app = App.useApp();

    if (form === undefined)
        throw new Error('Value of the `form` property is unknown');

    const fetchSuppliers = useCallback(async () => {
        setIsFetching(true);

        const response = await listSuppliers();
        
        setIsFetching(false);

        if (!response.success) {
            app.notification.error({
                message: 'Erro',
                description: 'Falha ao carregar fornecedores.',
            });
            return [];
        }

        setSuppliers(response.suppliers);
        return response.suppliers;
    }, [app]);

    const handleCreateSupplier = async () => {

        const values = await drawerForm.validateFields();

        setIsCreating(true);

        const clients = Authentication.getClients().map((client) => client.id);

        const response = await createSupplier({
            ...values,
            clientsToSync: clients,
        });

        await sleep(500);

        setIsCreating(false);
       
        app.notification.open(response);

        const updatedSuppliers = await fetchSuppliers();
        const created = updatedSuppliers?.find((s) => s.name === values.name);

        if (created) {
            if (multiple) {
                const current = form.getFieldValue(name) || [];
                form.setFieldValue(name, [...current, created.id]);
            } else {
                form.setFieldValue(name, created.id);
            }
        }

        drawerForm.resetFields();
        setDrawerVisible(false);
    };

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const parsedSuppliers = suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
    }));

    return (
        <Fragment>
            <Form.Item
                name={name}
                label={label}
                rules={rules}
                extra={extra}
                style={{ marginBottom: 0 }}
            >
                <Select
                    mode={multiple ?? undefined}
                    options={parsedSuppliers}
                    loading={isFetching}
                    disabled={isFetching}
                    allowClear
                    filterOption={makeFilterOption()}
                    showSearch
                    placeholder="Selecione um fornecedor"
                    dropdownRender={(menu) => (
                        <Fragment>
                            {menu}
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ padding: '8px', textAlign: 'center' }}>
                                <Button
                                    type="primary"
                                    onClick={() => setDrawerVisible(true)}
                                    block
                                >
                                    Cadastrar novo fornecedor
                                </Button>
                            </div>
                        </Fragment>
                    )}
                />
            </Form.Item>

            <Drawer
                title="Cadastrar fornecedor"
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                width={400}
            >
                <Form layout="vertical" form={drawerForm} onFinish={handleCreateSupplier}>
                    <Form.Item name="name" label="Nome" rules={[{ required: true, message: 'Descreva' }]}>
                        <AntdInput />
                    </Form.Item>

                    <Form.Item name="cnpj" label="CNPJ">
                        <MaskedInput mask={'00.000.000/0000-00'} />
                    </Form.Item>

                    <Form.Item name="email" label="Email">
                        <AntdInput type='email' />
                    </Form.Item>

                    <WhatsAppNumberField name="phone_1" />

                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isCreating}
                        block
                        style={{ marginTop: 16 }}
                    >
                        Salvar
                    </Button>
                </Form>
            </Drawer>
        </Fragment>
    );
}
